// =============================================================================
// MeuDriver — XPProgressBar
// Barra de XP com nível atual e threshold do próximo nível
// =============================================================================

import { motion } from 'framer-motion'
import { Zap } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserXP } from '@/hooks/useAchievements'
import { cn } from '@/lib/utils'

/** XP necessário para subir de level n para n+1: level^2 * 100 */
function xpForLevel(level: number): number {
  return level * level * 100
}

/** XP acumulado até o início do level n: soma de xpForLevel(1..n-1) */
function xpAtLevelStart(level: number): number {
  let total = 0
  for (let l = 1; l < level; l++) total += xpForLevel(l)
  return total
}

interface Props {
  className?: string
}

export function XPProgressBar({ className }: Props) {
  const { data, isLoading } = useUserXP()

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-full rounded-full" />
      </div>
    )
  }

  if (!data) return null

  const { xp_points, level, current_streak } = data

  const levelStart = xpAtLevelStart(level)
  const levelEnd   = levelStart + xpForLevel(level)
  const xpInLevel  = xp_points - levelStart
  const xpNeeded   = xpForLevel(level)
  const pct        = Math.min(100, Math.round((xpInLevel / xpNeeded) * 100))

  return (
    <div
      className={cn(
        'rounded-2xl border border-brand-100 bg-gradient-to-r from-brand-50 to-teal-50 p-4',
        className,
      )}
      data-testid="xp-progress-bar"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white text-sm font-extrabold shadow">
            {level}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 leading-none">Nível {level}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {xp_points.toLocaleString('pt-BR')} XP
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {current_streak > 0 && (
            <div className="flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-700 border border-orange-200">
              <Zap className="h-3 w-3" />
              {current_streak} semanas
            </div>
          )}
          <p className="text-xs text-muted-foreground hidden sm:block">
            Próximo: {(levelEnd - xp_points).toLocaleString('pt-BR')} XP
          </p>
        </div>
      </div>

      <div className="h-2.5 bg-white/70 rounded-full overflow-hidden border border-brand-100">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full bg-gradient-to-r from-brand-500 to-teal-500"
        />
      </div>

      <p className="text-[11px] text-muted-foreground mt-1.5 text-right">
        {xpInLevel.toLocaleString('pt-BR')} / {xpNeeded.toLocaleString('pt-BR')} XP para o Nível {level + 1}
      </p>
    </div>
  )
}
